import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { types as api } from '@mesa-labs/mesa-api';
import { DateTime } from 'luxon';

export const REV_SHARE_STATS_MIN_DATE = '2026-01-01T00:00:00.000Z';

function clampToMinDate(value?: string): string | undefined {
  if (!value) return value;
  return DateTime.fromISO(value) < DateTime.fromISO(REV_SHARE_STATS_MIN_DATE)
    ? REV_SHARE_STATS_MIN_DATE
    : value;
}

// Unlike clampToMinDate, this never returns undefined -- callers making the actual
// getOperatorPartnerRevShareStatistics request use this so an unset filter (the default state,
// or after Clear) still sends a beginningAt no earlier than REV_SHARE_STATS_MIN_DATE, rather
// than omitting the param and letting the backend return unrestricted history.
export function resolveStatsBeginningAt(value?: string): string {
  return clampToMinDate(value) ?? REV_SHARE_STATS_MIN_DATE;
}

export type PartnerRevSharesState = {
  // Config list state
  partnerId?: api.Partners;
  includeExpired: boolean;
  // Statistics state
  statsPartnerId?: api.Partners;
  statsPeriodization: api.TimeSeriesPeriodization;
  statsBeginningAt?: string;
  statsEndingAt?: string;
};

export const initialState: PartnerRevSharesState = {
  partnerId: undefined,
  includeExpired: false,
  statsPartnerId: undefined,
  statsPeriodization: api.TimeSeriesPeriodization.WEEK,
  statsBeginningAt: undefined,
  statsEndingAt: undefined,
};

const partnerRevSharesSlice = createSlice({
  name: 'partnerRevShares',
  initialState,
  reducers: {
    updatePartnerId(state, action: PayloadAction<api.Partners | undefined>) {
      state.partnerId = action.payload;
    },
    updateIncludeExpired(state, action: PayloadAction<boolean>) {
      state.includeExpired = action.payload;
    },
    updateStatsPartnerId(state, action: PayloadAction<api.Partners | undefined>) {
      state.statsPartnerId = action.payload;
    },
    updateStatsPeriodization(state, action: PayloadAction<api.TimeSeriesPeriodization>) {
      state.statsPeriodization = action.payload;
    },
    updateStatsBeginningAt(state, action: PayloadAction<string | undefined>) {
      state.statsBeginningAt = clampToMinDate(action.payload);
    },
    updateStatsEndingAt(state, action: PayloadAction<string | undefined>) {
      state.statsEndingAt = action.payload;
    },
  },
});

export const {
  updatePartnerId,
  updateIncludeExpired,
  updateStatsPartnerId,
  updateStatsPeriodization,
  updateStatsBeginningAt,
  updateStatsEndingAt,
} = partnerRevSharesSlice.actions;

export default partnerRevSharesSlice.reducer;
